'use client';

import { useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '@/store/app';
import { api } from '@/lib/api';

interface HeatmapResp {
  symbol: string;
  interval: string;
  currentPrice: number | null;
  priceBins: { min: number; max: number }[];
  timeSlots: number[];
  grid: (number | null)[][];
  rowMax: number[];
  total: number;
  longTotal: number;
  shortTotal: number;
}

interface LiqRow {
  symbol: string;
  exchange: string;
  side: string;
  price: number;
  value: number;
  time: string;
}

const HOT_SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT', 'DOGEUSDT', 'ADAUSDT', 'LINKUSDT', 'SUIUSDT', 'PEPEUSDT'];

function fmtVal(n: number): string {
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M';
  if (n >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'K';
  return '$' + n.toFixed(0);
}

function fmtPrice(n: number): string {
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (n >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return n.toLocaleString('en-US', { maximumFractionDigits: 5 });
}

function heatColor(v: number, max: number): string {
  if (v <= 0 || max <= 0) return 'rgba(96,108,140,0.08)';
  const t = Math.min(v / max, 1);
  // deep blue -> purple -> red
  const r = Math.round(20 + 210 * t);
  const g = Math.round(80 + 20 * t);
  const b = Math.round(160 - 130 * t);
  return `rgba(${r},${g},${b},${0.25 + 0.75 * t})`;
}

/** D09: missing 格 — 斜线/点纹背景（无观测，不是零）。 */
const MISSING_STYLE: CSSProperties = {
  backgroundColor: 'rgba(96,108,140,0.06)',
  backgroundImage:
    'repeating-linear-gradient(45deg, rgba(120,134,168,0.35) 0 2px, transparent 2px 5px)',
};

/** D09: 覆盖原因文案 — 区分「无事件(有效0)」与「没有数据(missing)」。 */
const COVERAGE_REASON_ZH = '格内「斜线」= 该时段/价格带无观测数据（数据源未覆盖，非零值）；浅色格 = 观测到 0';
const COVERAGE_REASON_EN = 'Diagonal cells = no observed data in this slot/price band (not zero); light cells = observed 0';

export default function LiqHeatmap() {
  const { language } = useAppStore();
  const isZh = language !== 'en';
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [input, setInput] = useState('');
  const [interval, setInterval] = useState<'1h' | '4h' | '24h'>('24h');
  // AUD16: clicked cell -> detail overlay
  const [selected, setSelected] = useState<{ pIdx: number; tIdx: number } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['liq-heatmap', symbol, interval],
    queryFn: () => api.get<HeatmapResp>(`/liquidations/heatmap?symbol=${symbol}&interval=${interval}`),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // R05: cell drill-down fetches the exact time window (same as heatmap aggregation),
  // so summary total and detail rows are same-scope; price bucket filtered client-side.
  const { data: liqRows, isFetching: detailLoading } = useQuery({
    queryKey: ['liq-rows', symbol, selected?.tIdx, selected?.pIdx],
    queryFn: () => {
      const slots = data?.timeSlots ?? [];
      const slotMs = slots.length > 1 ? slots[1] - slots[0] : 3600_000;
      const t0ms = slots[selected!.tIdx] ?? 0;
      return api.get<LiqRow[]>(`/liquidations?symbol=${symbol}&limit=500&from=${t0ms / 1000}&to=${(t0ms + slotMs) / 1000}`);
    },
    enabled: !!selected && (data?.timeSlots?.length ?? 0) > 1,
    staleTime: 30_000,
  });

  const submitSymbol = (s: string) => {
    const clean = s.trim().toUpperCase();
    if (!clean) return;
    const sym = clean.endsWith('USDT') ? clean : clean + 'USDT';
    setSymbol(sym);
  };

  const grid = data?.grid ?? [];
  const rowMax = data?.rowMax ?? [];
  const priceBins = data?.priceBins ?? [];
  const timeSlots = data?.timeSlots ?? [];
  const total = data?.total ?? 0;
  const longTotal = data?.longTotal ?? 0;
  const shortTotal = data?.shortTotal ?? 0;

  // D09: 覆盖统计（有观测格 / 总格），部分缺失时显示复盖数量。
  const coverage = useMemo(() => {
    let observed = 0;
    let all = 0;
    for (const row of grid) for (const v of row) { all++; if (v != null) observed++; }
    return { observed, all };
  }, [grid]);

  // R05: rows inside the selected cell — backend already filtered to the slot
  // window; here only the price bucket is applied client-side.
  const detailRows = useMemo(() => {
    if (!selected || !liqRows || !timeSlots.length || !priceBins.length) return [];
    const b = priceBins[selected.pIdx];
    return liqRows
      .filter((r) => {
        // r.time is already epoch millis (13-digit) from the API; do NOT multiply.
        const ts = Number(r.time);
        const slotMs = timeSlots.length > 1 ? timeSlots[1] - timeSlots[0] : 3600_000;
        const t0 = timeSlots[selected.tIdx];
        const t1 = t0 + slotMs;
        return ts >= t0 && ts < t1 && r.price >= b.min && r.price < b.max;
      })
      .sort((a, b2) => Number(b2.time) - Number(a.time));
  }, [selected, liqRows, timeSlots, priceBins]);

  const selectedCellValue = selected && grid[selected.pIdx] ? grid[selected.pIdx][selected.tIdx] ?? null : null;

  const closeDetail = () => setSelected(null);
  const selectHot = (s: string) => {
    setSymbol(s);
    closeDetail();
  };

  const t = {
    liqHeatmap: isZh ? '清算热力图' : 'Liquidation Heatmap',
    symbol: isZh ? '币种' : 'Symbol',
    interval: isZh ? '区间' : 'Interval',
    price: isZh ? '价格' : 'Price',
    total: isZh ? '总爆仓' : 'Total',
    long: isZh ? '多单' : 'Long',
    short: isZh ? '空单' : 'Short',
    noData: isZh ? '该区间暂无爆仓数据，切换区间或稍后再试' : 'No liquidation data in this window. Try another interval.',
    current: isZh ? '当前价' : 'Last',
  };

  return (
    <div>
      {/* Controls */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="hidden flex-wrap gap-1 sm:flex">
          {HOT_SYMBOLS.map((s) => (
            <button
              key={s}
              onClick={() => selectHot(s)}
              className={`rounded-full border px-2.5 py-1 text-[10px] ${symbol === s ? 'border-gf-accent bg-gf-accent/15 text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'}`}
            >
              {s.replace('USDT', '')}
            </button>
          ))}
        </div>
        <select
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          className="input !h-8 !w-32 !py-0 text-xs sm:hidden"
          aria-label={t.symbol}
        >
          {HOT_SYMBOLS.map((s) => (
            <option key={s} value={s}>{s.replace('USDT', '')}</option>
          ))}
        </select>
        <div className="flex items-center gap-1 rounded-full border border-gf-border bg-gf-surface2 px-1.5 py-1">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitSymbol(input)}
            placeholder={isZh ? '输入币对' : 'Enter pair'}
            className="w-20 bg-transparent text-[11px] outline-none placeholder:text-gf-muted"
          />
          <button onClick={() => submitSymbol(input)} className="text-[11px] text-gf-accent">Go</button>
        </div>
        <div className="ml-auto flex gap-1">
          {(['1h', '4h', '24h'] as const).map((iv) => (
            <button
              key={iv}
              onClick={() => { setInterval(iv); closeDetail(); }}
              className={`rounded-full border px-3 py-1 text-[10px] ${interval === iv ? 'border-gf-accent bg-gf-accent/15 text-gf-accent' : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'}`}
            >
              {iv}
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="mb-3 grid grid-cols-3 gap-2">
        <div className="card !p-2.5">
          <div className="text-[9px] text-gf-muted">{t.total}</div>
          <div className="font-mono text-sm font-semibold">{fmtVal(total)}</div>
        </div>
        <div className="card !p-2.5">
          <div className="text-[9px] text-gf-muted">{t.long}</div>
          <div className="font-mono text-sm font-semibold text-gf-positive">{fmtVal(longTotal)}</div>
        </div>
        <div className="card !p-2.5">
          <div className="text-[9px] text-gf-muted">{t.short}</div>
          <div className="font-mono text-sm font-semibold text-gf-negative">{fmtVal(shortTotal)}</div>
        </div>
      </div>

      {/* Heatmap */}
      <div className="card overflow-x-auto !p-3">
        {/* D13: 手机横向滚动提示 */}
        <div className="mb-1.5 flex items-center justify-between gap-2 sm:hidden">
          <span className="text-[10px] text-gf-muted">{isZh ? '左右滑动查看更多时段' : 'Swipe sideways for more time slots'}</span>
          <span className="text-[10px] text-gf-accent/70">⟷</span>
        </div>
        {isLoading && !grid.length ? (
          <div className="py-8 text-center text-xs text-gf-muted">{isZh ? '加载中...' : 'Loading...'}</div>
        ) : grid.length && priceBins.length ? (
          <div className="min-w-[560px]">
            {/* AUD16: coverage header — actual window + market source */}
            <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gf-muted">
              <span>
                {isZh ? '范围' : 'Range'}:{' '}
                <span className="font-mono">
                  {new Date(timeSlots[0]).toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}
                  {' – '}
                  {new Date(timeSlots[timeSlots.length - 1] + (timeSlots.length > 1 ? timeSlots[1] - timeSlots[0] : 3600_000)).toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}{' '}
                  (UTC{new Date().getTimezoneOffset() <= 0 ? '+' : '-'}{Math.abs(new Date().getTimezoneOffset() / 60)})
                </span>
              </span>
              <span>
                {isZh ? '来源' : 'Source'}:{' '}
                <span className="font-mono">Binance · Bitget · OKX · Gate</span>
              </span>
              <span className="text-gf-accent/80">
                {isZh ? '已发生的爆仓分布（非预测）' : 'Observed liquidation distribution (not a forecast)'}
              </span>
            </div>
            {/* Header: time slots */}
            <div className="mb-1 grid" style={{ gridTemplateColumns: `76px repeat(${timeSlots.length}, 1fr)` }}>
              <div className="text-[9px] text-gf-muted">{t.symbol} · {symbol}</div>
              {timeSlots.map((ts, i) => (
                <div key={ts} className="text-center font-mono text-[10px] text-gf-muted">
                  {new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                </div>
              ))}
            </div>
            {/* Rows: price buckets — price label sticky on horizontal scroll */}
            <div className="space-y-[3px]">
              {priceBins.map((b, pIdx) => (
                <div key={pIdx} className="grid" style={{ gridTemplateColumns: `76px repeat(${timeSlots.length}, 1fr)` }}>
                  <div className="sticky left-0 z-[1] flex items-center bg-gf-surface pr-1.5 font-mono text-[9px] text-gf-muted">
                    {fmtPrice(b.min)}-{fmtPrice(b.max)}
                  </div>
                  {(grid[pIdx] || []).map((v, tIdx) => {
                    const missing = v == null;
                    return (
                      <button
                        key={tIdx}
                        type="button"
                        onClick={() => setSelected({ pIdx, tIdx })}
                        aria-label={`${symbol} ${fmtPrice(priceBins[pIdx].min)}-${fmtPrice(priceBins[pIdx].max)} ${new Date(timeSlots[tIdx]).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })} ${missing ? '无数据' : fmtVal(v)}`}
                        title={`${symbol} | ${new Date(timeSlots[tIdx]).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })} | $${fmtPrice(priceBins[pIdx].min)}-$${fmtPrice(priceBins[pIdx].max)} | ${missing ? (isZh ? '无观测数据（数据源未覆盖）' : 'No observed data (source not covered)') : fmtVal(v)}`}
                        className={`h-5 rounded-[3px] transition-transform hover:scale-y-110 hover:ring-1 hover:ring-gf-accent ${
                          selected?.pIdx === pIdx && selected?.tIdx === tIdx ? 'ring-2 ring-gf-accent' : ''
                        }`}
                        style={missing ? MISSING_STYLE : { backgroundColor: heatColor(v as number, rowMax[pIdx] ?? 1) }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-xs text-gf-muted">{t.noData}</div>
        )}
        {data?.currentPrice != null && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-gf-muted">
            <span>
              {t.current}: <span className="font-mono text-gf-text">${fmtPrice(data.currentPrice)}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-sm" style={MISSING_STYLE} />
              <span>{isZh ? '缺失（无观测）' : 'Missing (no data)'}</span>
              <span className="inline-block h-2 w-2 rounded-sm border border-gf-border" style={{ backgroundColor: 'rgba(96,108,140,0.08)' }} />
              <span>{isZh ? '零（有效）' : 'Zero (valid)'}</span>
              <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: 'rgba(20,80,160,0.6)' }} />
              <span>{isZh ? '低' : 'Low'}</span>
              <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: 'rgba(230,60,30,0.9)' }} />
              <span>{isZh ? '高' : 'High'}</span>
            </span>
            <span title={isZh ? COVERAGE_REASON_ZH : COVERAGE_REASON_EN}>
              {coverage.all > 0
                ? (isZh
                    ? `覆盖 ${coverage.observed}/${coverage.all} 格${coverage.observed < coverage.all ? '（部分缺失）' : ''}`
                    : `Coverage ${coverage.observed}/${coverage.all}${coverage.observed < coverage.all ? ' (partial)' : ''}`)
                : ''}
            </span>
            <span>
              {isZh ? '点击格子查看该时段 / 价格区间明细' : 'Click a cell for its time-slot / price-bucket details'}
            </span>
          </div>
        )}
      </div>

      {/* AUD16: cell detail overlay — centered, never off-screen */}
      {selected && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" onClick={closeDetail}>
          <div
            className="card max-h-[80vh] w-full max-w-md overflow-y-auto !p-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={isZh ? '爆仓明细' : 'Liquidation details'}
          >
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold">
                  {symbol} · {fmtPrice(priceBins[selected.pIdx].min)}–{fmtPrice(priceBins[selected.pIdx].max)}
                </div>
                <div className="mt-0.5 text-[10px] text-gf-muted">
                  {new Date(timeSlots[selected.tIdx]).toLocaleString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}
                  {' – '}
                  {new Date(timeSlots[selected.tIdx] + (timeSlots.length > 1 ? timeSlots[1] - timeSlots[0] : 3600_000)).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                  {' (UTC'}{new Date().getTimezoneOffset() <= 0 ? '+' : '-'}{Math.abs(new Date().getTimezoneOffset() / 60)}{')'}
                </div>
                <div className="mt-0.5 text-[10px] text-gf-muted">
                  {isZh ? '已发生爆仓，来源' : 'Observed liquidations from'}: Binance · Bitget · OKX · Gate
                </div>
              </div>
              <button
                type="button"
                onClick={closeDetail}
                aria-label={isZh ? '关闭' : 'Close'}
                className="shrink-0 rounded-md border border-gf-border bg-gf-surface2 px-2 py-1 text-xs text-gf-muted hover:text-gf-text"
              >
                ✕
              </button>
            </div>
            {detailLoading ? (
              <div className="py-6 text-center text-xs text-gf-muted">{isZh ? '载入明细...' : 'Loading rows...'}</div>
            ) : detailRows.length === 0 ? (
              <div className="py-6 text-center text-xs text-gf-muted">
                {selectedCellValue == null
                  ? (isZh ? '该格无观测数据（数据源未覆盖，非零值）' : 'No observed data in this cell (source not covered, not zero)')
                  : selectedCellValue > 0
                    ? (isZh ? '该格有爆仓金额但未取到明细，请稍后重试' : 'Cell has value but no detail rows — retry later')
                    : (isZh ? '该时段 / 价格区间无爆仓' : 'No liquidations in this slot / price bucket')}
              </div>
            ) : (
              <ul className="space-y-1">
                {detailRows.slice(0, 50).map((r, i) => (
                  <li key={i} className="flex items-center gap-2 rounded-md bg-gf-surface2/60 px-2 py-1.5 text-xs">
                    <span className={`shrink-0 rounded px-1 py-px text-[9px] font-medium ${r.side === 'long' ? 'bg-gf-negative/15 text-gf-negative' : 'bg-gf-positive/15 text-gf-positive'}`}>
                      {r.side === 'long' ? (isZh ? '多' : 'Long') : (isZh ? '空' : 'Short')}
                    </span>
                    <span className="font-mono text-gf-muted">{r.exchange}</span>
                    <span className="ml-auto font-mono">${fmtPrice(r.price)}</span>
                    <span className="w-16 text-right font-mono">{fmtVal(r.value)}</span>
                  </li>
                ))}
                {detailRows.length > 50 && (
                  <li className="px-2 py-1 text-center text-[10px] text-gf-muted">
                    {isZh ? `仅显示前 50 条，共 ${detailRows.length} 条` : `Showing first 50 of ${detailRows.length}`}
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
